import { useQuery } from "@tanstack/react-query";
import { CircularProgress, Stack, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { BaseTable } from "../../../components/table";
import { BranchTablePropsType } from "../../../types/container.type";
import DeleteIcon from '@mui/icons-material/Delete';
import { BaseButton } from "../../../components/button/styled";
import Cookies from "universal-cookie";
import { retrieveStateByIdService } from "../../../utils/outbound/state/stateById";
import { retrieveUserProfileService } from "../../../utils/api/usermanagement/profile/retrieveUser";
import { retrieveInstitutionByIdService } from "../../../utils/outbound/institution/retrieveInstitutionById";
import { retrieveCountryByIdService } from "../../../utils/outbound/country/countryById";
import { retrieveOtherStatesService } from "../../../utils/outbound/other/states";

export const BranchTable: React.FC<BranchTablePropsType> = ({ rows, handleItemClick, selectedBranchId }) => {
    const branchTableHeaders = ["Branch ID", "Name of Branch", "Parent Branch", "State", "Action"];
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const { data: user } = useQuery({
        queryKey: ['userProfile', TOKEN],
        queryFn: () => retrieveUserProfileService(TOKEN),
        enabled: !!TOKEN,
    });

    const fetchStateById = async (stateId: string) => {
        if (!user?._id) return null;
        const institution = await retrieveInstitutionByIdService(user?._id, user?.institution);
        const country = await retrieveCountryByIdService(user?._id, institution.country);
        if (country?.title === "Nigeria") {
            const response = await retrieveStateByIdService(user._id, stateId);
            return response?.title ?? "Unknown";
        } else {
            const response = await retrieveOtherStatesService({ country: country?.title });
            return response?.data?.states?.[Number(stateId) - 1]?.name ?? "Unknown";
        }
    };

    const { data: stateNames, isLoading: isStateLoading } = useQuery({
        queryKey: ['stateNames', rows, user?._id],
        queryFn: async () => {
            if (!rows || !user?._id) return {};
            const statePromises = rows?.map(async (row) => ({
                [row.id]: await fetchStateById(row.state),
            }));
            const resolvedStates = await Promise.all(statePromises);
            return Object.assign({}, ...resolvedStates); // Merge array into an object
        },
        enabled: !!user?._id && rows?.length > 0,
    });

    return (
        <BaseTable headers={branchTableHeaders}>
            <TableBody>
                {rows?.map((row, index) => (
                    <TableRow key={index}>
                        <TableCell>{row?.serialNo.toString().padStart(3, '0')}</TableCell>
                        <TableCell>{row?.name}</TableCell>
                        <TableCell>{row?.parent ?? "None"}</TableCell>
                        <TableCell>
                            {isStateLoading ?
                                <CircularProgress color="inherit" className="loader" />
                                : stateNames?.[row.id]}
                        </TableCell>
                        <TableCell>
                            <Stack direction="row" gap="calc(var(--flex-gap)/4)">
                                <BaseButton
                                    variant="contained"
                                    bgcolor="var(--primary-color)"
                                    padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
                                    fontweight={500}
                                    fontsize="14px"
                                    onClick={(e) => handleItemClick?.(e, row.id, "edit")}
                                >
                                    <Typography
                                        variant="button"
                                        fontFamily="inherit"
                                        fontWeight="inherit"
                                        fontSize="inherit"
                                        lineHeight="inherit"
                                        color="inherit"
                                        textTransform="inherit"
                                    >
                                        Edit
                                    </Typography>
                                </BaseButton>
                                <BaseButton
                                    variant="contained"
                                    bgcolor="var(--text-salmon-pink-color)"
                                    padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
                                    fontweight={500}
                                    fontsize="14px"
                                    onClick={(e) => handleItemClick?.(e, { branchId: row.id, institutionId: row.institution }, "delete")}
                                >
                                    {selectedBranchId !== null && selectedBranchId === row.id ? (
                                        <CircularProgress color="inherit" className="loader" />
                                    ) : (
                                        <DeleteIcon />
                                    )}
                                </BaseButton>
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </BaseTable>
    );
};
